import type { JobChangeHistoryWithRelations } from '@maker-toolchest/shared';
import { formatDistanceToNow } from 'date-fns';
import { Clock } from 'lucide-react';

interface ChangeHistoryProps {
  history: JobChangeHistoryWithRelations[];
  loading?: boolean;
}

export function ChangeHistory({ history, loading }: ChangeHistoryProps) {
  if (loading) {
    return (
      <div className="rounded border border-border bg-background-subtle p-8 text-center">
        <p className="text-foreground-muted">Loading history...</p>
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <div className="rounded border border-border bg-background-subtle p-8 text-center">
        <Clock className="h-10 w-10 mx-auto mb-2 text-foreground-muted" />
        <p className="text-foreground-muted">No change history yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {history.map((change) => (
        <div
          key={change.id}
          className="rounded border border-border bg-background-subtle p-3"
        >
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-foreground capitalize">
                  {change.changeType.replace(/_/g, ' ')}
                </span>
                {change.fieldName && (
                  <span className="text-xs text-foreground-muted">
                    ({change.fieldName})
                  </span>
                )}
              </div>
              {change.message && (
                <p className="mt-1 text-sm text-foreground-muted">{change.message}</p>
              )}
              {(change.oldValue || change.newValue) && (
                <div className="mt-2 flex items-center gap-2 text-xs font-mono">
                  {change.oldValue && (
                    <span className="rounded bg-error/10 px-2 py-1 text-error line-through">
                      {change.oldValue}
                    </span>
                  )}
                  {change.oldValue && change.newValue && (
                    <span className="text-foreground-muted">→</span>
                  )}
                  {change.newValue && (
                    <span className="rounded bg-success/10 px-2 py-1 text-success">
                      {change.newValue}
                    </span>
                  )}
                </div>
              )}
              <div className="mt-2 flex items-center gap-2 text-xs text-foreground-muted">
                <span>
                  {change.user ? change.user.name : 'System'}
                </span>
                <span>·</span>
                <span>
                  {formatDistanceToNow(new Date(change.createdAt), { addSuffix: true })}
                </span>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
